/**
 * dismissal — device-local (localStorage) per-surface dismissal with a 14-day
 * re-surface (T12.6.1 Deliverable 5).
 *
 * A dismissible location ask — the Deals inline row, the ModeSpine
 * post-manual-switch hint — records WHEN it was dismissed, keyed by its own
 * surface name. Reading it back re-shows the ask once 14 days have elapsed, so
 * a "not now" tap doesn't silence the ask forever, only long enough to not nag
 * on every visit. Best-effort + SSR-safe (mirrors `deviceSettings.ts`); the
 * clock is injectable (`now`) so the 14-day boundary is deterministic in tests.
 */

const DISMISS_WINDOW_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

function keyFor(surface: string): string {
  return `shopista.dismissed.${surface}`;
}

/** Record that `surface`'s ask was dismissed now (or at the injected `now`). */
export function dismissSurface(surface: string, now: number = Date.now()): void {
  try {
    window.localStorage.setItem(keyFor(surface), String(now));
  } catch {
    // Best-effort — in-memory only this render if storage is unavailable.
  }
}

/**
 * Whether `surface`'s ask is currently suppressed — dismissed less than 14
 * days ago (per the injected `now`). `false` when never dismissed, unreadable,
 * or the window has elapsed (the ask re-surfaces).
 */
export function isSurfaceDismissed(
  surface: string,
  now: number = Date.now(),
): boolean {
  try {
    const raw = window.localStorage.getItem(keyFor(surface));
    if (!raw) return false;
    const dismissedAt = Number(raw);
    if (!Number.isFinite(dismissedAt)) return false;
    return now - dismissedAt < DISMISS_WINDOW_MS;
  } catch {
    return false;
  }
}
